import { useMemo } from "react";

import qs from "qs";

import { useRoleContext } from "./useRoleContext";
import { useShipmentFiltersContext } from "./useShipmentFiltersContext";
import { useSwrFetch } from "./useSwrFetch";

interface GetSupplierShipmentsResponse {
  results: SupplierShipment[];
  meta: { count: number };
}

interface PaginationProps {
  page: number;
  pageSize: number;
}

const buildSupplierShipmentFilterQueryParameters = (
  filters: ShipmentFilters | null,
  offset: number | null,
  limit: number | null,
) => {
  const filtersObject = {
    equipment_type: filters?.equipment_types ?? null,
    service: filters?.services ?? null,
    commodity: filters?.commodity?.code ?? null,
    status: filters?.status ?? null,
    order_by: filters?.order_by ?? null,
    origin_province: filters?.origin?.provinces ?? null,
    origin_country: filters?.origin?.countries ?? null,
    destination_province: filters?.destination?.provinces ?? null,
    destination_country: filters?.destination?.countries ?? null,
    offset: offset ?? null,
    limit: limit ?? null,
    shipment_name:
      filters?.shipmentName && filters.shipmentName !== ""
        ? filters.shipmentName
        : null,
    // Note: Supplier shipments don't support shipment_type filter as they are all SINGLE
    // Note: Supplier shipments don't support admin filters
    // (branches, shippers, include_company_shipments, include_branch_shipments)
  };

  return qs.stringify(filtersObject, {
    skipNulls: true,
    arrayFormat: "repeat",
  });
};

export function useSupplierShipments(props?: PaginationProps) {
  let offset: number | null = null;
  let limit: number | null = null;
  if (props !== undefined) {
    const { page, pageSize } = props;
    limit = pageSize;
    offset = (page - 1) * limit;
  }

  const { role } = useRoleContext();
  const { filters } = useShipmentFiltersContext("supplier");

  const url = useMemo(() => {
    if (!role?.id) return null;
    const filtersQueryString = buildSupplierShipmentFilterQueryParameters(
      filters,
      offset,
      limit,
    );
    return `/shippers/${role.id}/supplier-shipments?${filtersQueryString}`;
  }, [role?.id, filters, offset, limit]);

  const {
    data,
    loading: isLoading,
    error,
    refetch: refreshSupplierShipments,
  } = useSwrFetch<GetSupplierShipmentsResponse>({
    url,
    errorMessage: "Failed to fetch supplier shipments",
  });

  return {
    supplierShipments: data?.results ?? [],
    isLoading,
    error,
    count: data?.meta?.count ?? 0,
    refreshSupplierShipments,
  };
}
